import { useState } from 'react'
import { X, ChevronDown, ChevronUp } from 'lucide-react'
import { APP_VERSION } from '@/version'
import { changelog } from '@/data/changelog'
import logoSmall from '@/assets/logo-small.png'

interface AboutModalProps {
  onClose: () => void
}

export default function AboutModal({ onClose }: AboutModalProps) {
  const [showChangelog, setShowChangelog] = useState(false)

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 'var(--z-modal)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-xl)',
          width: '100%',
          maxWidth: 480,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.25rem',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--color-text)' }}>
            About Twasta.ai
          </span>
          <button
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ overflowY: 'auto', padding: '1.5rem 1.25rem', flex: 1 }}>
          {/* Logo + name */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem',
            }}
          >
            <img
              src={logoSmall}
              alt="Twasta.ai"
              style={{ width: 72, height: 72, objectFit: 'contain' }}
            />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.3125rem', fontWeight: 700, color: 'var(--color-text)' }}>
                Twasta.ai
              </div>
              <div
                style={{
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-secondary)',
                  marginTop: '0.125rem',
                }}
              >
                Version {APP_VERSION}
              </div>
            </div>
          </div>

          {/* Description */}
          <p
            style={{
              fontSize: '0.9375rem',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.6,
              marginBottom: '1.5rem',
              textAlign: 'center',
            }}
          >
            Twasta.ai is a comprehensive low-code / no-code application generation platform.
            Design, generate, and deploy complete business applications through AI-powered tools,
            visual designers, and intelligent workflows — without writing a single line of code.
          </p>

          {/* What's New button */}
          <button
            onClick={() => setShowChangelog(!showChangelog)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '0.625rem 0.875rem',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: showChangelog ? 'var(--color-bg-secondary)' : 'transparent',
              color: 'var(--color-text)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all var(--transition-fast)',
            }}
          >
            <span>What's New</span>
            {showChangelog ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {/* Changelog */}
          {showChangelog && (
            <div style={{ marginTop: '0.75rem' }}>
              {changelog.map((entry) => (
                <div key={entry.version} style={{ marginBottom: '1rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8125rem',
                        fontWeight: 700,
                        color: 'var(--color-accent)',
                        background: 'var(--color-accent-weak)',
                        padding: '0.125rem 0.5rem',
                        borderRadius: 'var(--radius-full)',
                      }}
                    >
                      v{entry.version}
                    </span>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                      {entry.date}
                    </span>
                  </div>
                  <ul
                    style={{
                      paddingLeft: '1.125rem',
                      margin: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                    }}
                  >
                    {entry.changes.map((change, i) => (
                      <li
                        key={i}
                        style={{
                          fontSize: '0.875rem',
                          color: 'var(--color-text-secondary)',
                          lineHeight: 1.5,
                        }}
                      >
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '0.875rem 1.25rem',
            borderTop: '1px solid var(--color-border)',
            textAlign: 'center',
            fontSize: '0.8125rem',
            color: 'var(--color-text-secondary)',
          }}
        >
          © {new Date().getFullYear()} Proteus Technologies Pvt. Ltd. All rights reserved.
        </div>
      </div>
    </div>
  )
}
